import React, { Fragment, FunctionComponent, MouseEvent, useEffect, useState } from "react";

import { CONST_LEFT_MOUSE_BTN, CONST_RIGHT_MOUSE_BTN, IPoint, LinkPassableProps } from "@multiweb/picasso";
import { bemClassNames, createQA } from "@multiweb/reactUtils";
import { PropagationStopper } from "../components/dialogs/components";
import { TransitionEditDialog } from "../components/dialogs/editDialogs";
import { ITransition } from "../types";
import { Line } from "./Line";
import { Point } from "./Point";
import { calcAngleDegrees, getWfStateCodes, textEllipsis } from "./utils";

export type TransitionComponentProps = {
    readonly transition: ITransition;
    readonly isWorkflowReadOnly: boolean;
    readonly loadTransitionHeader: (picassoInstanceKey: string, picassoIndex: number, attributeValues: readonly string[]) => void;
} & Pick<LinkPassableProps, "link" | "isSelected" | "isAnyPointSelected" | "onLinkClick" | "selectPoint" | "updatePoints" | "picassoInstanceKey">;

const classNames = bemClassNames("wf-transition");
const ghostPointClassName = classNames("ghost-point");
const breakPointClassName = classNames("break-point");
const headerClassName = classNames("header");

export const TransitionComponent: FunctionComponent<TransitionComponentProps> = ({ transition, picassoInstanceKey, link, isWorkflowReadOnly, isSelected, isAnyPointSelected, onLinkClick, selectPoint, updatePoints, loadTransitionHeader }) => {
    const [isEditDialogShown, setIsEditDialogShown] = useState(false);

    const lineFragments: JSX.Element[] = [];
    const linePoints: JSX.Element[] = [];

    const points = link.points;
    const middlePointIndex = points.length >> 1;
    const hasAction = transition.actions.length > 0;
    const hasCondition = !!transition.condition;
    const isActive = transition.isActive;

    const arrowHeadId = `triangle${transition.id}`;
    const lineClassName = classNames({ "condition": hasCondition, "has-actions": hasAction, "selected": isSelected, "active": isActive });
    const invisibleLineClassName = classNames("invisible-transition", { "selected": isSelected, "is-any-point-selected": isAnyPointSelected });
    const invisiblePointClassName = classNames("invisible-point", { "is-any-point-selected": isAnyPointSelected });
    const arrowHeadClassName = classNames("arrow-head", { "has-actions": hasAction, "selected": isSelected, "active": isActive });

    useEffect(() => {
        const wfStateCodes = getWfStateCodes(transition.actions);
        if (wfStateCodes.length)
            loadTransitionHeader(picassoInstanceKey, transition.picassoIndex, wfStateCodes);
    }, [transition.actions, transition.picassoIndex, picassoInstanceKey, loadTransitionHeader]);

    /** Otevírá editační formulář propojení. */
    const onCloseEdit = () => {
        setIsEditDialogShown(false);
    };

    /** Událost se stará o vybrání pointu, na který bylo právě kliknuto. */
    const handleMouseDown = (e: MouseEvent, index: number, lastPosition: IPoint) => {
        if (e.buttons === CONST_LEFT_MOUSE_BTN && !isWorkflowReadOnly)
            selectPoint(index, { x: e.clientX, y: e.clientY }, lastPosition);
    };

    /** Událost se stará o otevírání editačního dialogu. */
    const onDoubleClick = (e: MouseEvent<SVGElement>) => {
        if (!e.ctrlKey && isSelected) // Zabránění případu otevření editačního dialogu při hromadném výběru...
            setIsEditDialogShown(true);
    };

    for (let i = 0; i < points.length - 1; i++) {
        const startPoint = points[i];
        const endPoint = points[i + 1];
        const ghostPoint = { x: (startPoint.x + endPoint.x) / 2, y: (startPoint.y + endPoint.y) / 2 };
        const pointId = transition.id + "_point_";
        const key = transition.id + "_line_" + i;

        /** Událost se stará o vytvoření break pointu z ghost pointu. */
        const handleGhostPointMouseDown = (e: MouseEvent) => {
            if (e.buttons !== CONST_LEFT_MOUSE_BTN || isWorkflowReadOnly)
                return;

            const newPoints = [...points];

            const newPoint: IPoint = {
                x: Math.round(ghostPoint.x),
                y: Math.round(ghostPoint.y),
            };

            newPoints.splice(i + 1, 0, newPoint);

            updatePoints(newPoints);
            selectPoint(i + 1, { x: e.clientX, y: e.clientY }, ghostPoint);
        };

        /** Událost se stará o smazání break pointu z transition. */
        const handleMouseUp = (e: MouseEvent) => {
            const nextPointIndex = i + 1;
            const isPressedPointTheLast = nextPointIndex === points.length - 1;

            if (isWorkflowReadOnly || isPressedPointTheLast || e.button !== CONST_RIGHT_MOUSE_BTN)
                return;

            const newPoints = points.filter((_point, index) => index !== nextPointIndex);
            updatePoints(newPoints);
        };

        lineFragments.push(
            <Line
                className={lineClassName}
                markerEnd={i + 2 === points.length ? `url(#${arrowHeadId})` : undefined} //je následující bod posledním bodem?
                x1={startPoint.x}
                y1={startPoint.y}
                x2={endPoint.x}
                y2={endPoint.y}
                title={transition.name}
                onClick={onLinkClick}
                {...{ key, invisibleLineClassName, isSelected, onDoubleClick }} />,
        );

        if (i + 1 === middlePointIndex)
            lineFragments.push(
                <text
                    key="header"
                    className={headerClassName}
                    onClick={onLinkClick}
                    onDoubleClick={onDoubleClick}
                    x={ghostPoint.x}
                    y={ghostPoint.y - 2}
                    transform={`rotate(${calcAngleDegrees(startPoint, endPoint)}, ${ghostPoint.x}, ${ghostPoint.y})`}>
                    <title>{transition.header}</title>
                    {textEllipsis(transition.header)}
                </text>,
            );

        if (!isSelected)
            continue;

        if (i === 0)
            linePoints.push(
                <Point
                    key={`${pointId}0`}
                    cx={startPoint.x}
                    cy={startPoint.y}
                    className={breakPointClassName}
                    {...{ invisiblePointClassName }}
                    onMouseDown={(e) => handleMouseDown(e, i, startPoint)} />,
            );

        if (i + 1 !== points.length)
            linePoints.push(
                <Point key={`${pointId}${i + 1}`} cx={endPoint.x} cy={endPoint.y} className={breakPointClassName} onMouseUp={handleMouseUp} onMouseDown={(e) => handleMouseDown(e, i + 1, endPoint)} {...{ invisiblePointClassName }} />,
                <Point key={`${pointId}${i + 1}_ghost`} cx={ghostPoint.x} cy={ghostPoint.y} className={ghostPointClassName} onMouseDown={handleGhostPointMouseDown} {...{ invisiblePointClassName }} />,
            );
    }

    lineFragments.push(...linePoints); // musím umístit do lineFragments až naposled, aby se pointy zobrazily nad čarami

    return (
        <Fragment>
            <g {...createQA(`transition${transition.id}`)} >
                <marker id={arrowHeadId} viewBox="0 0 8 8" refX="7" refY="4" markerWidth="8" markerHeight="8" markerUnits="strokeWidth" orient="auto" className={arrowHeadClassName}>
                    <path d="M 0 0 L 8 4 L 0 8 z" />
                </marker>
                {lineFragments}
            </g>
            {isEditDialogShown &&
                <PropagationStopper>
                    <TransitionEditDialog hostKey={picassoInstanceKey} picassoIndex={transition.picassoIndex} {...{ onCloseEdit }} />
                </PropagationStopper>
            }
        </Fragment>
    );
};
